/**
 * Components whose lifecycle belongs to one live browser/desktop window.
 * They may poll, join calls, hold authorization state, or represent a
 * transient warning. Persisting or broadcasting them can replay side effects
 * in another tab or resurrect stale work after login.
 */
export const EPHEMERAL_COMPONENT_NAMES = new Set([
  // Local system action cards own one live IPC request. Restoring or syncing
  // them could replay an old file read or command approval after relaunch.
  "DesktopSystemAction",
  "JobStatus",
  "SyncTracker",
  "GoogleDrivePicker",
  "OneDrivePicker",
  "VideoConference",
  "InactivityWarning",
  "GazeCalibration",
  // Billing gates and checkout surfaces are reconstructed from the current
  // authenticated payment state. Restoring or broadcasting them can replay a
  // stale checkout or make one renderer dismiss another renderer's gate.
  "SubscriptionRequired",
  "StripeCheckoutViewer",
  // Device approvals are signed live decisions rebuilt from the server's
  // pending list; restoring one could replay a stale approve countdown.
  "DeviceApproval",
  // The onboarding checklist window is rebuilt from the onboarding preference;
  // restoring a saved copy could resurrect a dismissed checklist.
  "GettingStarted",
  // A ring and an assistant permission request each wait on one live answer
  // in this renderer; a restored or broadcast copy would answer nothing.
  "IncomingCall",
  "PermissionRequest",
  // The team invite notice follows this session's sign-in result; a restored
  // copy would claim an invite is still waiting after it has been confirmed.
  "TeamInviteWaiting",
  // The desktop update window mirrors the updater in this app's main process;
  // a restored or synced copy would offer an update another install owns.
  "AppUpdate",
  // The voice download window waits on this page's Phonon model for an answer
  // this page is about to speak; a restored or synced copy would wait on nothing.
  "VoiceDownload",
  // A gesture control error describes this renderer's camera and vision
  // runtime; another tab or a later session has its own.
  "GestureStatus",
]);

export function isEphemeralComponentName(name: unknown): boolean {
  return typeof name === "string" && EPHEMERAL_COMPONENT_NAMES.has(name);
}

type ComponentLike = {
  id?: unknown;
  name?: unknown;
};

/**
 * Durable workspace broadcasts intentionally omit runtime-owned components.
 * Receiving one must therefore replace only the durable portion of a window,
 * while leaving that renderer's live monitors and authorization flows alone.
 */
export function mergeDurableComponentsWithLocalEphemeral<T extends ComponentLike>(
  incomingByWindow: Record<string, T[]>,
  localByWindow: Record<string, T[]>,
): Record<string, T[]> {
  const windowIds = new Set([
    ...Object.keys(incomingByWindow),
    ...Object.keys(localByWindow),
  ]);

  return Object.fromEntries(Array.from(windowIds).flatMap((windowId) => {
    const hasIncomingWindow = Object.prototype.hasOwnProperty.call(incomingByWindow, windowId);
    const incomingComponents = Array.isArray(incomingByWindow[windowId])
      ? incomingByWindow[windowId]
      : [];
    const incomingIds = new Set(
      incomingComponents
        .map((component) => component?.id)
        .filter((id): id is string => typeof id === "string"),
    );
    const localEphemeral = (Array.isArray(localByWindow[windowId])
      ? localByWindow[windowId]
      : [])
      .filter((component) => isEphemeralComponentName(component?.name))
      .filter((component) => typeof component?.id !== "string" || !incomingIds.has(component.id));

    return hasIncomingWindow || localEphemeral.length > 0
      ? [[windowId, [...incomingComponents, ...localEphemeral]] as const]
      : [];
  }));
}
